import {
  Sortable,
  SortableDragHandle,
  SortableItem,
} from "@/components/ui/sortable";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import type { ResolvedShortcut } from "@/lib/dashboard-home/resolve-shortcuts";
import { cn } from "@/lib/utils";
import { GripVertical, Trash2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { ShortcutPill } from "./ShortcutPill";
import { SHORTCUT_GRID_CLASS, shortcutGridStyle } from "./shortcut-grid";

/** Grille des raccourcis de la modale : glisser-déposer et suppression. */
export const ShortcutsEditor = ({
  items,
  highlightId,
  onReorder,
  onRemove,
}: {
  items: ResolvedShortcut[];
  /** Raccourci qui vient d'être ajouté : mis en évidence un instant. */
  highlightId: string | null;
  onReorder: (idsInOrder: string[]) => void;
  onRemove: (id: string) => void;
}) => {
  const vt = useVisibleT();
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!highlightId) return;
    listRef.current
      ?.querySelector(`[data-shortcut-id="${CSS.escape(highlightId)}"]`)
      ?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlightId]);

  if (items.length === 0) {
    return (
      <p className="mt-4 rounded-lg bg-slate-50 p-4 text-center text-sm text-slate-500">
        {vt(
          "bo.home.shortcuts.emptyManage",
          "Aucun raccourci pour l’instant. Ajoutez-en un ci-dessous.",
        )}
      </p>
    );
  }

  return (
    <Sortable
      value={items}
      orientation="mixed"
      onValueChange={(next) => onReorder(next.map((item) => item.id))}
    >
      <ul
        ref={listRef}
        className={cn(SHORTCUT_GRID_CLASS, "mt-4 gap-x-6 gap-y-2")}
        style={shortcutGridStyle(items.length)}
      >
        {items.map((item) => (
          <SortableItem key={item.id} value={item.id} asChild>
            <li className="min-w-0" data-shortcut-id={item.id}>
              <ShortcutPill
                icon={item.icon}
                label={item.label}
                className={cn(
                  "bg-white px-3 py-2 backdrop-blur-none transition-shadow duration-500",
                  item.id === highlightId &&
                    "border-primary ring-2 ring-primary/40 ring-offset-1",
                )}
                before={
                  <SortableDragHandle
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-6 shrink-0 text-slate-400 hover:bg-transparent"
                    aria-label={vt("bo.nav.reorder", "Réordonner")}
                  >
                    <GripVertical className="size-4" aria-hidden />
                  </SortableDragHandle>
                }
                after={
                  <button
                    type="button"
                    onClick={() => onRemove(item.id)}
                    className="shrink-0 rounded p-1 text-slate-400 transition-colors hover:text-red-600"
                    aria-label={vt(
                      "bo.home.shortcuts.remove",
                      "Supprimer le raccourci",
                    )}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                }
              />
            </li>
          </SortableItem>
        ))}
      </ul>
    </Sortable>
  );
};
